import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { AppointmentCard } from "@/components/appointments/AppointmentCard";
import { toast } from "sonner";
import {
  ClipboardList,
  Users,
  Search,
  Loader2,
  Ruler,
  RefreshCw,
} from "lucide-react";

interface Appointment {
  id: string;
  status: string;
  slot: {
    id: string;
    startAt: string;
    endAt: string;
    slotType: string;
  };
  area: {
    id: string;
    name: string;
  };
  address: {
    id: string;
    line1: string;
    city: string;
  };
  customer: {
    id: string;
    publicId: string;
    name: string;
    phone?: string;
  };
  assignedExec?: {
    id: string;
    publicId: string;
    name: string;
  } | null;
}

export default function StaffMeasurementsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [myAppointments, setMyAppointments] = useState<Appointment[]>([]);
  const [unassignedAppointments, setUnassignedAppointments] = useState<Appointment[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [claiming, setClaiming] = useState<string | null>(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  async function fetchAppointments() {
    try {
      const [myRes, unassignedRes] = await Promise.all([
        fetch("/api/staff/appointments/measurements?scope=my"),
        fetch("/api/staff/appointments/measurements?scope=unassigned"),
      ]);

      if (myRes.ok) {
        const data = await myRes.json();
        setMyAppointments(data.appointments || []);
      }

      if (unassignedRes.ok) {
        const data = await unassignedRes.json();
        setUnassignedAppointments(data.appointments || []);
      }
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await fetchAppointments();
  }

  async function handleClaim(appointmentId: string) {
    setClaiming(appointmentId);
    try {
      const response = await fetch(`/api/staff/appointments/${appointmentId}/claim`, {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to claim appointment");
      }

      toast.success("Appointment claimed successfully!");
      await fetchAppointments();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to claim");
    } finally {
      setClaiming(null);
    }
  }

  async function handleSearch() {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await fetch(
        `/api/admin/measurements?customerPublicId=${encodeURIComponent(searchQuery.trim())}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Customer not found");
          setSearchResult(null);
          return;
        }
        throw new Error("Search failed");
      }

      const data = await response.json();
      setSearchResult(data);
    } catch (error) {
      toast.error("Failed to search");
      setSearchResult(null);
    } finally {
      setSearching(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Measurement Visits" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Measurement Visits"
        actions={
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />
            Customer Lookup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder="Enter Customer ID (e.g., HM-0001)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="max-w-xs"
            />
            <Button onClick={handleSearch} disabled={searching || !searchQuery.trim()}>
              {searching ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Search
            </Button>
          </div>

          {searchResult && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{searchResult.customer.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {searchResult.customer.publicId}
                    {searchResult.customer.phone && ` - ${searchResult.customer.phone}`}
                  </p>
                  {searchResult.hasProfile && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {searchResult.activeVersion
                        ? `Active measurement: v${searchResult.activeVersion.versionNo}`
                        : "No active measurements"}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate(`/customer/measurements/book?customerId=${searchResult.customer.id}`)}
                >
                  <Ruler className="h-4 w-4 mr-2" />
                  Create Visit
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="my" className="w-full">
        <TabsList>
          <TabsTrigger value="my" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            My Visits
            {myAppointments.length > 0 && (
              <span className="ml-1 text-xs bg-primary/20 px-1.5 rounded-full">
                {myAppointments.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="unassigned" className="gap-2">
            <Users className="h-4 w-4" />
            Unassigned
            {unassignedAppointments.length > 0 && (
              <span className="ml-1 text-xs bg-amber-500/20 text-amber-700 px-1.5 rounded-full">
                {unassignedAppointments.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my" className="mt-6">
          {myAppointments.length > 0 ? (
            <div className="space-y-4">
              {myAppointments.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  showCustomer
                  showExec={false}
                  onView={() => navigate(`/staff/measurements/${appointment.id}`)}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={ClipboardList}
                  title="No Assigned Visits"
                  description="You don't have any measurement visits assigned to you. Claim one from the unassigned queue."
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="unassigned" className="mt-6">
          {unassignedAppointments.length > 0 ? (
            <div className="space-y-4">
              {unassignedAppointments.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  showCustomer
                  showExec={false}
                  onClaim={() => handleClaim(appointment.id)}
                  onView={() => navigate(`/staff/measurements/${appointment.id}`)}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={Users}
                  title="No Unassigned Visits"
                  description="All measurement visits have been assigned. Check back later for new appointments."
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
